import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatPhoneOnBlur } from '@/lib/phone';
import { getStates } from '../api/leads-api';
import type { LeadFormData } from '../types';

interface AddressSectionProps {
  formData: LeadFormData;
  onChange: (field: keyof LeadFormData, value: string | number | boolean | undefined) => void;
}

function PhoneRow({
  id,
  label,
  value,
  notesId,
  notesValue,
  onChange,
  onNotesChange,
}: {
  id: string;
  label: string;
  value: string;
  notesId: string;
  notesValue: string;
  onChange: (value: string) => void;
  onNotesChange: (value: string) => void;
}) {
  return (
    <div>
      <Label htmlFor={id} className="text-[10px]">{label}</Label>
      <div className="flex gap-1">
        <Input
          id={id}
          type="tel"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onChange(formatPhoneOnBlur(e.target.value))}
          className="h-6 text-xs flex-[3]"
          placeholder="(555) 555-1234"
        />
        <Input
          id={notesId}
          value={notesValue}
          onChange={(e) => onNotesChange(e.target.value)}
          className="h-6 text-xs flex-[2]"
          placeholder="Notes"
        />
      </div>
    </div>
  );
}

export function AddressSection({ formData, onChange }: AddressSectionProps) {
  const states = getStates();
  const isSame = formData.hasSameAddress;

  return (
    <div className="grid grid-cols-4 gap-2">
      {/* Property Address */}
      <div>
        <h4 className="text-secondary font-semibold text-xs mb-1">Property Address</h4>
        <div className="space-y-0.5">
          <div>
            <Label htmlFor="address1" className="text-[10px]">Address 1 *</Label>
            <Input
              id="address1"
              value={formData.address1}
              onChange={(e) => onChange('address1', e.target.value)}
              required
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="address2" className="text-[10px]">Address 2</Label>
            <Input
              id="address2"
              value={formData.address2 || ''}
              onChange={(e) => onChange('address2', e.target.value)}
              className="h-6 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="city" className="text-[10px]">City *</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) => onChange('city', e.target.value)}
              required
              className="h-6 text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-1">
            <div>
              <Label htmlFor="state" className="text-[10px]">State *</Label>
              <Input
                id="state"
                value="OH"
                disabled
                className="bg-gray-50 h-6 text-xs"
              />
            </div>
            <div>
              <Label htmlFor="zip" className="text-[10px]">Zip *</Label>
              <Input
                id="zip"
                value={formData.zip}
                onChange={(e) => onChange('zip', e.target.value)}
                maxLength={10}
                required
                className="h-6 text-xs"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Billing Address - always visible, disabled when same as property */}
      <div>
        <h4 className="text-secondary font-semibold text-xs mb-1">Billing Address</h4>
        <div className="space-y-0.5">
          <div>
            <Label htmlFor="billingAddress1" className="text-[10px]">Address 1</Label>
            <Input
              id="billingAddress1"
              value={isSame ? formData.address1 : (formData.billingAddress1 || '')}
              onChange={(e) => onChange('billingAddress1', e.target.value)}
              disabled={isSame}
              className={cn("h-6 text-xs", isSame && "bg-gray-50")}
            />
          </div>
          <div>
            <Label htmlFor="billingAddress2" className="text-[10px]">Address 2</Label>
            <Input
              id="billingAddress2"
              value={isSame ? (formData.address2 || '') : (formData.billingAddress2 || '')}
              onChange={(e) => onChange('billingAddress2', e.target.value)}
              disabled={isSame}
              className={cn("h-6 text-xs", isSame && "bg-gray-50")}
            />
          </div>
          <div>
            <Label htmlFor="billingCity" className="text-[10px]">City</Label>
            <Input
              id="billingCity"
              value={isSame ? formData.city : (formData.billingCity || '')}
              onChange={(e) => onChange('billingCity', e.target.value)}
              disabled={isSame}
              className={cn("h-6 text-xs", isSame && "bg-gray-50")}
            />
          </div>
          <div className="grid grid-cols-2 gap-1">
            <div>
              <Label htmlFor="billingState" className="text-[10px]">State</Label>
              <Select
                id="billingState"
                value={isSame ? formData.state : (formData.billingState || '')}
                onChange={(e) => onChange('billingState', e.target.value)}
                disabled={isSame}
                className={cn("h-6 text-xs", isSame && "bg-gray-50")}
              >
                <option value="">-</option>
                {states.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.code}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="billingZip" className="text-[10px]">Zip</Label>
              <Input
                id="billingZip"
                value={isSame ? formData.zip : (formData.billingZip || '')}
                onChange={(e) => onChange('billingZip', e.target.value)}
                disabled={isSame}
                maxLength={10}
                className={cn("h-6 text-xs", isSame && "bg-gray-50")}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div>
        <h4 className="text-secondary font-semibold text-xs mb-1">Contact Information</h4>
        <div className="space-y-0.5">
          <PhoneRow
            id="homePhone"
            label="Home Phone"
            value={formData.homePhone || ''}
            notesId="homePhoneNotes"
            notesValue={formData.homePhoneNotes || ''}
            onChange={(v) => onChange('homePhone', v)}
            onNotesChange={(v) => onChange('homePhoneNotes', v)}
          />
          <PhoneRow
            id="cellPhone"
            label="Cell Phone"
            value={formData.cellPhone || ''}
            notesId="cellPhoneNotes"
            notesValue={formData.cellPhoneNotes || ''}
            onChange={(v) => onChange('cellPhone', v)}
            onNotesChange={(v) => onChange('cellPhoneNotes', v)}
          />
          <PhoneRow
            id="workPhone"
            label="Work Phone"
            value={formData.workPhone || ''}
            notesId="workPhoneNotes"
            notesValue={formData.workPhoneNotes || ''}
            onChange={(v) => onChange('workPhone', v)}
            onNotesChange={(v) => onChange('workPhoneNotes', v)}
          />
          <PhoneRow
            id="otherPhone"
            label="Other Phone"
            value={formData.otherPhone || ''}
            notesId="otherPhoneNotes"
            notesValue={formData.otherPhoneNotes || ''}
            onChange={(v) => onChange('otherPhone', v)}
            onNotesChange={(v) => onChange('otherPhoneNotes', v)}
          />
          <div>
            <Label htmlFor="email" className="text-[10px]">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => onChange('email', e.target.value)}
              className="h-6 text-xs"
            />
          </div>
        </div>
      </div>

      {/* Tenant Information */}
      <div>
        <h4 className="text-secondary font-semibold text-xs mb-1">Tenant Information</h4>
        <div className="space-y-0.5">
          <div>
            <Label htmlFor="tenantName" className="text-[10px]">Tenant Name</Label>
            <Input
              id="tenantName"
              value={formData.tenantName || ''}
              onChange={(e) => onChange('tenantName', e.target.value)}
              className="h-6 text-xs"
            />
          </div>
          <PhoneRow
            id="tenantHomePhone"
            label="Home Phone"
            value={formData.tenantHomePhone || ''}
            notesId="tenantHomePhoneNotes"
            notesValue={formData.tenantHomePhoneNotes || ''}
            onChange={(v) => onChange('tenantHomePhone', v)}
            onNotesChange={(v) => onChange('tenantHomePhoneNotes', v)}
          />
          <PhoneRow
            id="tenantCellPhone"
            label="Cell Phone"
            value={formData.tenantCellPhone || ''}
            notesId="tenantCellPhoneNotes"
            notesValue={formData.tenantCellPhoneNotes || ''}
            onChange={(v) => onChange('tenantCellPhone', v)}
            onNotesChange={(v) => onChange('tenantCellPhoneNotes', v)}
          />
          <PhoneRow
            id="tenantWorkPhone"
            label="Work Phone"
            value={formData.tenantWorkPhone || ''}
            notesId="tenantWorkPhoneNotes"
            notesValue={formData.tenantWorkPhoneNotes || ''}
            onChange={(v) => onChange('tenantWorkPhone', v)}
            onNotesChange={(v) => onChange('tenantWorkPhoneNotes', v)}
          />
          <PhoneRow
            id="tenantOtherPhone"
            label="Other Phone"
            value={formData.tenantOtherPhone || ''}
            notesId="tenantOtherPhoneNotes"
            notesValue={formData.tenantOtherPhoneNotes || ''}
            onChange={(v) => onChange('tenantOtherPhone', v)}
            onNotesChange={(v) => onChange('tenantOtherPhoneNotes', v)}
          />
        </div>
      </div>
    </div>
  );
}
